import { Links, Meta, useLoaderData, useLocation } from 'react-router';
import { ClientOnly } from 'remix-utils/client-only';

import InlineFonts from '~/components/InlineFonts';
import { AdmiralCmp } from '~/vendor/cx-web/components/third-party/admiral-cmp';
import { AdobeAnalyticsHead } from '~/vendor/cx-web/components/third-party/adobe-analytics';
import { GoogleTagManagerHead, shouldRenderGtm } from '~/vendor/cx-web/components/third-party/google-tag-manager';
import { VwoAnalytics } from '~/vendor/cx-web/components/third-party/vwo';
import { type FontAttribs, isLiveEnvironment } from '~/vendor/cx-web/domain-types';
import { featureFlags, useFeatureFlag } from '~/vendor/cx-web/feature-flags/feature-flag-provider';
import { useRepubContext } from '~/vendor/cx-web/feature-repub';
import { REPUB_CONFIG } from '~/vendor/cx-web/repub-config';

import { type loader } from './root-loader';

export const Favicon = ({ resourcesUrl }: { resourcesUrl: string }) => {
  const faviconPath = `${resourcesUrl}/assets/img/`;
  const { repub } = useRepubContext();

  const faviconFile = REPUB_CONFIG[repub]?.favicon || 'oc-favicon.svg';
  const favicon = `${faviconPath}${faviconFile}`;

  return (
    <>
      <link as="image" href={favicon} key={`preload-${favicon}`} rel="preload" />
      <link href={favicon} key={`icon-${favicon}`} rel="icon" />
    </>
  );
};

export const Fonts = () => {
  const baseFonts: FontAttribs[] = [
    // Public Sans, Lato
    {
      rel: 'preconnect',
      href: 'https://fonts.googleapis.com',
    },
    {
      rel: 'preconnect',
      href: 'https://fonts.gstatic.com',
      crossOrigin: 'anonymous',
    },
    // Futura PT Condensed
    {
      rel: 'preconnect',
      href: 'https://use.typekit.net',
    },
  ];

  return (
    <>
      {baseFonts.map(data => (
        <link key={`${data.rel}-${data.href}`} {...data} />
      ))}
    </>
  );
};

export default function Head() {
  const rootLoaderData = useLoaderData<typeof loader>();
  if (!rootLoaderData) {
    throw new Error('Root loader data is missing');
  }

  const { pathname } = useLocation();
  const disableAdobeAnalytics = useFeatureFlag(featureFlags.DISABLE_ADOBE_ANALYTICS);
  const disableGtm = useFeatureFlag(featureFlags.DISABLE_GOOGLE_TAG_MANAGER);
  const disableVwo = useFeatureFlag(featureFlags.DISABLE_VWO);
  const shouldRenderGTMPostHydration = useFeatureFlag(featureFlags.RENDER_GTM_POST_HYDRATION);

  const {
    ENV,
    admiralCmpUrl,
    baseUrl,
    cssHrefs,
    geolocationInfo,
    gtmId,
    isAdmiralCookieBannerEnabled,
    myocData,
    resourcesUrl,
  } = rootLoaderData;

  const { repub, repubIs } = useRepubContext();

  return (
    <head>
      <meta charSet="utf-8" />
      <meta content="width=device-width,initial-scale=1" name="viewport" />
      <meta content={`app-id=6459478704, app-argument=${pathname}`} name="apple-itunes-app" />
      <meta content="telephone=no" name="format-detection" />
      <base href={baseUrl} />
      <Meta />
      <AdmiralCmp admiralUrl={admiralCmpUrl} enabled={isAdmiralCookieBannerEnabled} />
      {isAdmiralCookieBannerEnabled && <link as="script" href={admiralCmpUrl} rel="preload" />}
      <link as="image" href={`${resourcesUrl}/assets/img/OC_LOGO_2.webp`} rel="preload" />
      {cssHrefs.map(href => (
        <link href={href} key={href} rel="stylesheet" />
      ))}
      <Links />
      <Favicon resourcesUrl={resourcesUrl} />
      <InlineFonts repub={repub} resourcesUrl={resourcesUrl} />
      <Fonts />
      {!REPUB_CONFIG[repub]?.tracking?.usesConsentGatedGTM && (
        <GoogleTagManagerHead
          geolocationInfo={geolocationInfo}
          gtmId={gtmId}
          repub={repub}
          shouldRender={!disableGtm && shouldRenderGtm(ENV.ENVIRONMENT)}
          shouldRenderPostHydration={shouldRenderGTMPostHydration}
          userGroup={myocData?.userGroup}
          userId={myocData?.myOCUser?.userID}
        />
      )}
      <ClientOnly fallback={null}>
        {() => <VwoAnalytics accountId={repubIs.US ? 780115 : 772951} disabled={disableVwo} />}
      </ClientOnly>
      {ENV.ENVIRONMENT !== 'LOCALHOST' &&
        ENV.ENVIRONMENT !== 'TEST' &&
        !REPUB_CONFIG[repub]?.tracking?.skipFingerprintScripts && (
          <>
            <script defer src={`${resourcesUrl.replace('/public', '')}/fingerprint/verify-client.js`} />
            <script defer src={`${resourcesUrl.replace('/public', '')}/nirvana/ping.js`} />
          </>
        )}
      {REPUB_CONFIG[repub]?.tracking?.usesRcsTagManager &&
        !disableAdobeAnalytics &&
        isLiveEnvironment(ENV.ENVIRONMENT) && <AdobeAnalyticsHead />}
    </head>
  );
}
